import React, { useState } from "react"
import Layout from "../../../components/layouts"
import Banner from "../../../components/features/Banner"
import SEO from "../../../components/common/SEO"
import ReactWOW from "react-wow"
import FeatureCards from "../../../components/features/FeatureCards"

export const query = graphql`
  query BehaviourObservations {
    site {
      siteMetadata {
        defaultTitle: title
        titleTemplate
        defaultDescription: description
        siteUrl: url
        defaultImage: image
        twitterUsername
      }
    }
  }
`

export default ({ data }) => {
  const [toggler, setToggler] = useState(false)
  const lightboxProps = {
    toggler: toggler,
    setToggler: setToggler,
    sources: ["https://www.youtube.com/watch?v=xE2yXvWB038"],
  }

  const bannerProps = {
    title: "Behavior Observations",
    subtitle: `Unsafe environmental conditions and mechanical failures are easy enough to manage with systems like inspections and hazard assessments, but what about incidents that might have been affected by human behavior, like fatigue, haste, distraction, poor habits, or workers simply taking a risk? How do you identify those hazards and build a culture to root them out? Systematic observations can help bring these behaviors and attitudes to light. Behavior observations can be considered hazard assessments of a worker’s mind and body.`,
    featureName: "behaviourObservations",
    rightButtonText: "Why Certificate Tracking Software?",
    rightButtonSrc: "https://www.youtube.com/watch?v=xE2yXvWB038",
    mockupImg: "/features/behavior-observations/feature-page-header-bbso.png",
  }

  const seo = {
    title: "Workhub | Free Behavior Observations Checklist Tool Safety BBSO Report",
    description:
      "Our Behavior-Based Safety tool lets you identify, submit, collect and trend data on risky or hazardous workplace behaviours so that you can improve employee safety and reduce indicidents/accidents.",
    image: "/features/behavior-observations/social-card.jpg",
    // pathname,
    // article
  }

  const featureIndex = [
    {
      title: "Behavior-Based Safety Observation",
      para: "Observe employees in their day-to-day activities to ensure that they are exhibiting behaviors and attitudes that contribute to a safe workplace.",
    },
    {
      title: "Identify ",
      para: "Identify behaviors that contribute to unwanted outcomes at your workplace and build checklists to sign-off on.",
    },
    {
      title: "Assign",
      para: "Assign observations by position and location.",
    },
    {
      title: "Designate",
      para: "Choose staff or supervisors that are qualified to assess other employees.",
    },
    {
      title: "Analyze the Results",
      para: "Filter your submissions by date and track the results over time. ",
    },

  ]

  return (
    <Layout {...lightboxProps}>
      <SEO {...seo} {...data.site} />
      <Banner {...bannerProps} {...lightboxProps} />

      <main className="feature-main">
        <section className="feature-section">
          <div className="container">
            <div className="row switch-children">
              <div className="col-lg-6 feature-img">
                <ReactWOW animation="fadeInLeft" delay="0.9s">
                  <img
                    className="feature-img-right"
                    src="/features/behavior-observations/bbso-comp-two.png"
                    alt="Behaviour Observation Computer View"
                  />
                </ReactWOW>
              </div>

              <div className="col-lg-6 feature-text">
                <h2>
                  Not pointing fingers ... 
                </h2>
                <p>
                  The concept of behavior-based safety isn’t meant to cause debate on whether workers are responsible for unsafe acts or if they are the root cause of injuries and quality issues. Instead, behavior-based safety simply suggests that undesirable behaviors can contribute to unwanted outcomes that can be identified and corrected. Not every organization will embrace behavior-based safety, but if you do, we have the tool to make it work.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="feature-section">
          <div className="container">
            <div className="row">
              <div className="col-lg-6 feature-text">
                <h2>
                  ... but gaining insight! 
                </h2>

                <p style={{ marginBottom: "0px" }}>
                  Creating a Behavior Observation checklist starts with identifying workplace behaviors that contribute to unwanted outcomes. Prior hazard assessments and incident reports are a good place to look for these behaviors, and reviewing root cause analysis may identify a few more. Don’t worry if your list isn’t very long, because Workhub has dozens of behaviour templates from which to pick and choose. Here are a few examples:
                </p>

                <ul className="checklist">
                  <li>Proper housekeeping (cleanliness, storage, access)</li>
                  <li>Correct tool or equipment for the task</li>
                  <li>PPE worn correctly</li>
                  <li>Mind on task (not distracted)</li>
                  <li>Not rushing</li>
                  <li>Not overexerting</li>
                  <li>Stretches and takes micro-breaks</li>
                  <li>Not lifting, bending or twisting</li>
                  <li>Avoid repetitive motions</li>
                  <li>Comfortable (not awkward) position</li>
                </ul>

                <p>
                  {" "}
                  Keep in mind when creating your list that behaviors are{" "}
                  <strong>
                    <em>not</em>
                  </strong>{" "}
                  task-specific, so it isn’t useful to add a behavior for “Operates backhoe safely”. (Our <a target="_blank" href="https://www.workhub.com/features/competencies/">Competency</a> tool is what you’ll want to use for task-specific assessments.)
                </p>
              </div>

              <div className="col-lg-6 feature-img">
                <ReactWOW animation="fadeInLeft" delay="0.9s">
                  <img
                    className="feature-img-left"
                    src="/features/behavior-observations/bbso-comp-3.png"
                    alt="Behavior Observations Checklist View"
                  />
                </ReactWOW>
              </div>
            </div>
          </div>
        </section>

        <section className="feature-section">
          <div className="container">
            <div className="row switch-children">
              <div className="col-lg-6 feature-img">
                <ReactWOW animation="fadeInLeft" delay="0.9s">
                  <img
                    className="feature-img-right"
                    src="/features/behavior-observations/bbso-mobile.png"
                    alt="Behaviour Observation Mobile View"
                  />
                </ReactWOW>
              </div>

              <div className="col-lg-6 feature-text">
                <h2>
                  Set up your template
                </h2>
                <p>
                  After creating your checklist, you can decide whether all behaviors need to be observed or whether some are not always applicable to each observation. If you decide N/A is not a valid choice, then “Safe” and “At Risk” become the only available selections. You can group the behaviors into sections, such as Body Mechanics, Job Setup, or Executing Work, and sort your behaviors within each grouping.  After tailoring your checklist, you can assign it to your organization by Position or by Location, depending on your needs. You can set it as optional or required and set a frequency for how often it needs completion. 
                </p>
                <p>
                  It’s important to note that Behavior Observations aren’t meant to identify the worker being observed. Rather, they’re intended as an insight into your workplace’s overall safe practices. Workers can be provided recommendations along the way, but observations shouldn’t single them out!
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="feature-section">
          <div className="container">
            <div className="row">
              <div className="col-lg-6 feature-text">
                <h2>
                  Let your workers observe
                </h2>

                <p>
                  Before firing up Behavior Observations, you might want to assign our <a target="_blank" href="https://www.workhub.com/features/training/behavior-based-safety/">Behavior Based Safety course</a> in Online Training, or maybe write a <a target="_blank" href="https://www.workhub.com/features/policies/">Policy</a> or <a target="_blank" href="https://www.workhub.com/features/standard-operating-procedures/">Procedure</a> on how they should be done. But, we digress...
                </p>

                <p>
                  Workers with permissions for Behavior Observations can navigate to the dash module and choose to start an observation. It’s usually a good idea to let the coworker know they’re being observed doing their job, and feedback can be provided along the way (or at least afterwards!). The observation form is optimized for mobile devices, so workers can observe anywhere they need. Observers identify the Location they’re observing and choose Safe, At Risk, or N/A for each check and can leave comments for each. 
                </p>

                <p>
                  After submission, statistics will start to pile up on the Admin dashboard. They’ll give you insight into what behaviors are most at risk to you can tailor your training, procedures, safety meetings, bulletins, and other tools to help mitigate hazards and reinforce desired behaviors!
                </p>
              </div>

              <div className="col-lg-6 feature-img">
                <ReactWOW animation="fadeInLeft" delay="0.9s">
                  <img
                    className="feature-img-left"
                    src="/features/behavior-observations/bbso-tablet.png"
                    alt="Behaviour Observation Tablet View"
                  />
                </ReactWOW>
              </div>
            </div>
          </div>
        </section>

        {/* FEATURE OVERVIEW */}

        <section className="feature-overview-container">
          <h2>Overview</h2>

          <div className="feature-overview-grid">
            {featureIndex.map(item => (
              <FeatureCards title={item.title} para={item.para} />
            ))}
          </div>
        </section>


      </main>
    </Layout>
  )
}
